import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Paper,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import StarIcon from "@mui/icons-material/Star";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";

// ─── helpers ──────────────────────────────────────────────────────────────────

function initials(fname = "", lname = "") {
  return `${fname[0] || ""}${lname[0] || ""}`.toUpperCase();
}

const AVATAR_PALETTE = [
  { bgcolor: "#E6F1FB", color: "#185FA5" },
  { bgcolor: "#E1F5EE", color: "#0F6E56" },
  { bgcolor: "#FAEEDA", color: "#854F0B" },
  { bgcolor: "#FBEAF0", color: "#993556" },
];

const avatarStyle = (i) => AVATAR_PALETTE[i % AVATAR_PALETTE.length];

// Average a guest's review ratings, or null if they have none.
function averageRating(reviews) {
  if (!reviews || reviews.length === 0) return null;
  const sum = reviews.reduce((acc, r) => acc + (r.rating || 0), 0);
  return sum / reviews.length;
}

// Rating -> chip color, so a manager can spot a problem guest at a glance.
function ratingChipColor(avg) {
  if (avg == null) return "default";
  if (avg >= 4) return "success";
  if (avg >= 3) return "warning";
  return "error";
}

// ─── component ────────────────────────────────────────────────────────────────

/**
 * GuestSearchStep — Step 3
 *
 * Fetches potential matches automatically on mount.
 * Manager must select an existing guest OR "Create new" before saving.
 *
 * Props:
 *   form: object    — confirmed form from IDUploadForm
 *   onBack()        — navigate back to IDUploadForm
 *   onComplete({ action: "created" | "linked", guest }) — final save callback
 *
 * Expected API endpoints:
 *   GET  /api/guests/search?fname&lname&phone&email&dob
 *        → { matches: [{ id, fname, lname, email, phone, visits, lastSeen,
 *                         reviews: [{ id, rating, comment, createdAt, createdBy }] }] }
 *        NOTE: reviews are expected inline on each match (same as visits/lastSeen)
 *        so the guest table can show prior reviews without an extra round trip.
 *        If your backend instead exposes a separate endpoint (e.g.
 *        GET /api/guests/:id/reviews), swap the per-row logic below to fetch
 *        lazily on expand instead of reading match.reviews directly.
 *
 *   POST /api/guests
 *        body: form
 *        → { guest }
 *
 *   POST /api/guests/:id/checkin
 *        body: { form }
 *        → { guest }
 */
export default function GuestSearchStep({ form, onBack, onComplete }) {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [matches, setMatches]   = useState([]);
  const [error, setError]       = useState("");
  const [selected, setSelected] = useState(null); // number | "new" | null
  const [expandedReviewsId, setExpandedReviewsId] = useState(null); // guest.id | null

  // ── auto-fetch on mount ───────────────────────────────────────────────────

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    setError("");
    setSelected(null);
    setExpandedReviewsId(null);
    try {
      const res = await axios.post("/api/searchCustomers", {
        
          fname: form.fname,
          lname: form.lname,
          phone: form.phone,
          email: form.email,
          dob:   form.dob,
      });
      setMatches(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to search guests. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  // ── save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (selected === "new") {
        const res = await axios.post("/api/guests", form);
        onComplete({ action: "created", guest: res.data.guest });
      } else {
        const res = await axios.post(`/api/guests/${selected}/checkin`, { form });
        onComplete({ action: "linked", guest: res.data.guest });
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save. Please try again.");
      setSaving(false);
    }
  };

  // ── derived ───────────────────────────────────────────────────────────────

  const selectedGuest =
    typeof selected === "number" ? matches.find((g) => g.id === selected) : null;

  const saveLabel = () => {
    if (selected === null)  return "Select a guest to continue";
    if (selected === "new") return "Create new guest profile";
    return `Link to ${selectedGuest?.fname}'s record`;
  };

  const toggleReviews = (e, guestId) => {
    e.stopPropagation();
    setExpandedReviewsId((prev) => (prev === guestId ? null : guestId));
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          px: 3, py: 1,
          borderBottom: "1px solid", borderColor: "divider",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2,
        }}
      >
        <Box>
          <Typography variant="body" fontWeight={600}>
            Select an existing guest or create a new profile
          </Typography>
          
        </Box>
        <Chip
          label="Selection required"
          color="warning"
          size="small"
          sx={{ fontSize: 12, mt: 0.5, flexShrink: 0 }}
        />
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Identity pills */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
          {[
            ["Name",  `${form.fname} ${form.lname}`.trim()],
            ["Phone", form.phone],
            ["DOB",   form.dob],
            ["Doc #", form.docNum],
          ]
            .filter(([, v]) => v?.trim())
            .map(([label, val]) => (
              <Chip
                key={label}
                size="small"
                variant="outlined"
                label={
                  <span>
                    <span style={{ opacity: 0.6 }}>{label} </span>
                    <strong>{val}</strong>
                  </span>
                }
                sx={{ fontSize: 12 }}
              />
            ))}
        </Box>

        {/* Error */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={<Button size="small" onClick={fetchMatches}>Retry</Button>}
          >
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="body" color="text.secondary" mt={1.5}>
              Searching guest records…
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              variant="overline"
              color="text.secondary"
              fontSize={11}
              display="block"
              mb={1}
            >
              {matches.length > 0
                ? `${matches.length} possible match${matches.length !== 1 ? "es" : ""} found`
                : "No existing matches — you can create a new profile below"}
            </Typography>

            {/* Table */}
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 1.5, mb: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "background.default" }}>
                    <TableCell padding="checkbox" />
                    <TableCell
                      sx={{
                        fontSize: 11, fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                      }}
                    >
                      Guest
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 11, fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                      }}
                    >
                      Phone
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 11, fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                      }}
                    >
                      Visit history
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 11, fontWeight: 600,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                      }}
                    >
                      Reviews
                    </TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {/* Existing guest rows */}
                  {matches.map((guest, idx) => {
                    const checked = selected === guest.id;
                    const av = avatarStyle(idx);
                    const reviews = guest.reviews || [];
                    const avg = averageRating(reviews);
                    const hasBadReview = reviews.some((r) => (r.rating || 0) <= 2);
                    const reviewsOpen = expandedReviewsId === guest.id;

                    return (
                      <>
                        <TableRow
                          key={guest.id}
                          hover
                          selected={checked}
                          onClick={() => setSelected(checked ? null : guest.id)}
                          sx={{ cursor: "pointer", ...(checked && { bgcolor: "primary.50" }) }}
                        >
                          <TableCell padding="checkbox">
                            <Radio
                              checked={checked}
                              size="small"
                              onChange={() => setSelected(guest.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                              
                              <Box>
                                <Typography variant="body" fontSize={13} fontWeight={500}>
                                  {guest.fname} {guest.lname}
                                </Typography><br></br>
                                <Typography variant="body" fontSize={12}  color="text.secondary">
                                  {guest.email || "—"}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={13} variant="body" color="text.secondary">
                              {guest.phone || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={12} variant="body" color="text.secondary">
                              {guest.visits ?? 0} visit{guest.visits !== 1 ? "s" : ""}
                              {guest.lastSeen ? ` · ${guest.lastSeen}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {reviews.length > 0 ? (
                              <Box
                                sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
                                onClick={(e) => toggleReviews(e, guest.id)}
                              >
                                <Chip
                                  size="small"
                                  color={ratingChipColor(avg)}
                                  icon={hasBadReview ? <WarningAmberIcon sx={{ fontSize: 14 }} /> : <StarIcon sx={{ fontSize: 14 }} />}
                                  label={`${avg.toFixed(1)} (${reviews.length})`}
                                  sx={{ fontSize: 11, fontWeight: 600 }}
                                />
                                <IconButton size="small" sx={{ p: 0.25 }}>
                                  {reviewsOpen ? (
                                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                                  ) : (
                                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </Box>
                            ) : (
                              <Typography fontSize={12} color="text.secondary">
                                No reviews
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {checked ? (
                              <Typography fontSize={12} fontWeight={500} color="primary.main">
                                ✓ Selected
                              </Typography>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: "none", fontSize: 12, py: 0.25 }}
                                onClick={(e) => { e.stopPropagation(); setSelected(guest.id); }}
                              >
                                Select
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Expanded review detail */}
                        {reviews.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0, border: reviewsOpen ? undefined : "none" }}>
                              <Collapse in={reviewsOpen} timeout="auto" unmountOnExit>
                                <Box
                                  sx={{
                                    px: 3, py: 1.5,
                                    bgcolor: "background.default",
                                    borderTop: "1px dashed", borderColor: "divider",
                                  }}
                                >
                                  {hasBadReview && (
                                    <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
                                      This guest has at least one low review — read before linking this record.
                                    </Alert>
                                  )}
                                  {reviews
                                    .slice()
                                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                    .map((r) => (
                                      <Box
                                        key={r.id}
                                        sx={{
                                          mb: 1.25,
                                          "&:last-of-type": { mb: 0 },
                                        }}
                                      >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <Box sx={{ display: "flex", alignItems: "center", color: ratingChipColor(r.rating) === "error" ? "error.main" : "text.primary" }}>
                                            {Array.from({ length: 5 }).map((_, i) => (
                                              <StarIcon
                                                key={i}
                                                sx={{
                                                  fontSize: 14,
                                                  color: i < (r.rating || 0) ? "inherit" : "action.disabled",
                                                }}
                                              />
                                            ))}
                                          </Box>
                                          <Typography fontSize={11} color="text.secondary">
                                            {r.createdBy ? `${r.createdBy} · ` : ""}
                                            {r.createdAt || ""}
                                          </Typography>
                                        </Box>
                                        {r.comment && (
                                          <Typography fontSize={12.5} sx={{ mt: 0.25 }}>
                                            {r.comment}
                                          </Typography>
                                        )}
                                      </Box>
                                    ))}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}

                  {/* Create new row */}
                  {(() => {
                    const isNew = selected === "new";
                    return (
                      <TableRow
                        hover
                        selected={isNew}
                        onClick={() => setSelected(isNew ? null : "new")}
                        sx={{ cursor: "pointer", ...(isNew && { bgcolor: "success.50" }) }}
                      >
                        <TableCell padding="checkbox">
                          <Radio
                            checked={isNew}
                            size="small"
                            color="success"
                            onChange={() => setSelected("new")}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell colSpan={4}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PersonAddIcon
                              sx={{
                                fontSize: 18,
                                color: isNew ? "success.main" : "text.secondary",
                              }}
                            />
                            <Typography
                              fontSize={13}
                              fontWeight={isNew ? 500 : 400}
                              color={isNew ? "success.main" : "text.secondary"}
                            >
                              Create a new guest profile for {form.fname} {form.lname}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {isNew ? (
                            <Typography fontSize={12} fontWeight={500} color="success.main">
                              ✓ Selected
                            </Typography>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              sx={{ textTransform: "none", fontSize: 12, py: 0.25 }}
                              onClick={(e) => { e.stopPropagation(); setSelected("new"); }}
                            >
                              Select
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Selection summary */}
            {selected !== null && (
              <Box
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: 2, py: 1.5, mb: 1,
                  bgcolor: "background.default",
                  border: "0.5px solid", borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                {selectedGuest ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 12, ...avatarStyle(0) }}>
                        {initials(selectedGuest.fname, selectedGuest.lname)}
                      </Avatar>
                      <Box>
                        <Typography fontSize={13} fontWeight={500}>
                          {selectedGuest.fname} {selectedGuest.lname}
                        </Typography>
                        <Typography fontSize={12} color="text.secondary">
                          Check-in will be added to their existing record
                          {selectedGuest.visits != null
                            ? ` · ${selectedGuest.visits} previous visit${selectedGuest.visits !== 1 ? "s" : ""}`
                            : ""}
                          {selectedGuest.reviews?.length
                            ? ` · ${averageRating(selectedGuest.reviews).toFixed(1)}★ avg (${selectedGuest.reviews.length} review${selectedGuest.reviews.length !== 1 ? "s" : ""})`
                            : ""}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label="Existing guest"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  </>
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 32, height: 32, fontSize: 12,
                          bgcolor: "#E1F5EE", color: "#0F6E56",
                        }}
                      >
                        {initials(form.fname, form.lname)}
                      </Avatar>
                      <Box>
                        <Typography fontSize={13} fontWeight={500}>
                          {form.fname} {form.lname}
                        </Typography>
                        <Typography fontSize={12} color="text.secondary">
                          A new guest profile will be created
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label="New guest"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  </>
                )}
              </Box>
            )}
          </>
        )}

        {/* Actions */}
        <Box
          sx={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", mt: 2.5,
          }}
        >
          <Button onClick={onBack} sx={{ textTransform: "none", color: "text.secondary" }}>
            ← Back to review
          </Button>
          <Button
            variant="contained"
            disabled={selected === null || loading || saving}
            onClick={handleSave}
            sx={{ textTransform: "none", minWidth: 200 }}
          >
            {saving && <CircularProgress size={14} sx={{ mr: 1, color: "inherit" }} />}
            {saveLabel()}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}